import { clsx } from 'clsx';
import { CSSProperties, FocusEvent, forwardRef, LegacyRef, ReactElement, Ref } from 'react';
import {
  Text as AriaText,
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  FieldError,
  Input,
  Label,
} from 'react-aria-components';
import IconButton from '~/components/buttons/NewIconButton';
import fieldStyles from '~/components/inputs/Field.module.scss';
import useDataTestId from '~/hooks/useDataTestId';
import IconClose from '~/icons/icon-close.svg?react';
import styles from './TextField.module.scss';

export interface TextFieldProps {
  autoComplete?: AriaTextFieldProps['autoComplete'];
  autoFocus?: boolean;
  /**
   * Optional accessible name to expose via `aria-label`. Useful when `label`
   * is empty (e.g. tabular layouts where the column header is the visible
   * label) and you still want the field to be reachable by assistive tech.
   */
  ariaLabel?: string;
  className?: string;
  endAddon?: React.ReactNode;
  errorMessage?: string;
  helpText?: string;
  hideLabel?: boolean;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  EndIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  id?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label: string;
  maxLength?: number;
  name?: string;
  onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
  onBlur?: (e: FocusEvent<Element, Element>) => void;
  onFocus?: (e: FocusEvent<Element, Element>) => void;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onKeyUp?: AriaTextFieldProps['onKeyUp'];
  onKeyDown?: AriaTextFieldProps['onKeyDown'];
  onPaste?: AriaTextFieldProps['onPaste'];
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  startAddon?: string | ReactElement;
  startAddonClassName?: string;
  endAddonClassName?: string;
  style?: CSSProperties;
  value: string;
  InputProps?: {
    maxLength?: number;
    className?: string;
    ref?: LegacyRef<HTMLInputElement> | undefined;
    style?: CSSProperties;
    type?: React.HTMLInputTypeAttribute;
  };
  dataTestId?: string;
  forceIgnore1Password?: boolean; // Used to ignore 1Password in some cases
}

const TextField = forwardRef(
  (
    {
      autoComplete,
      autoFocus,
      ariaLabel,
      className,
      endAddon,
      errorMessage,
      helpText,
      hideLabel,
      Icon,
      EndIcon,
      id,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      label,
      maxLength,
      name,
      onClick,
      onBlur,
      onFocus,
      onChange,
      onKeyUp,
      onKeyDown,
      onPaste,
      onClear,
      placeholder,
      size = 'medium',
      startAddon,
      startAddonClassName,
      endAddonClassName,
      style,
      value,
      InputProps,
      dataTestId,
      forceIgnore1Password,
    }: TextFieldProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const dt = useDataTestId(dataTestId);
    return (
      <AriaTextField
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        ref={ref}
        aria-label={hideLabel ? ariaLabel || label || undefined : undefined}
        className={clsx(fieldStyles.field, styles.inputField, className)}
        data-size={size}
        id={id}
        isDisabled={isDisabled}
        isInvalid={isInvalid || !!errorMessage}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        maxLength={maxLength}
        name={name}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        onClick={onClick}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        style={style}
        value={value}
        data-testid={dt()}
        validationBehavior="aria"
      >
        {!hideLabel && (
          <Label>
            {label}
            {isRequired && ' *'}
          </Label>
        )}
        <div className={styles.addonWrapper}>
          {startAddon && <div className={clsx(styles.addon, styles.startAddon, startAddonClassName)}>{startAddon}</div>}
          <div className={styles.inputWrapper}>
            {Icon && <Icon className={styles.icon} />}
            <Input
              className={clsx(InputProps?.className, { [styles.clearable!]: !!onClear })}
              placeholder={placeholder}
              maxLength={InputProps?.maxLength}
              type={InputProps?.type}
              ref={InputProps?.ref}
              data-testid={dt('input')}
              style={InputProps?.style}
              data-1p-ignore={forceIgnore1Password}
            />
            {!!value && onClear && (
              <IconButton onClick={onClear} className={styles.clearIcon} Icon={IconClose} variant="ghost" />
            )}
            {EndIcon && <EndIcon className={styles.endIcon} />}
          </div>
          {endAddon && <div className={clsx(styles.addon, styles.endAddon, endAddonClassName)}>{endAddon}</div>}
        </div>
        {helpText && <AriaText slot="description">{helpText}</AriaText>}
        <FieldError>{errorMessage}</FieldError>
      </AriaTextField>
    );
  },
);

export default TextField;
